import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/ApiService';
import Button from '../ui/Button';
import { submitPassportScan } from '../../services/api/passport';
import { verificationLevelLabel } from './passportCopy';

interface PassportScanPanelProps {
  /** Called after a successful log so the parent can refresh its list. */
  onLogged?: () => void;
  className?: string;
}

/**
 * Web manual code / paste-URL path to log a Bilt Passport encounter.
 * Secondary to the mobile camera scan flow — no camera capture here.
 */
const PassportScanPanel: React.FC<PassportScanPanelProps> = ({ onLogged, className = '' }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = code.trim();
    if (!value) return;
    setSubmitting(true);
    void apiService.trackEvent('passport_scan_started', { metadata: { source_platform: 'web' } });
    try {
      const result = await submitPassportScan({
        payload: value,
        method: 'manual_code',
        client_scan_id: `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });

      if (result.owner_self) {
        showToast("That's your own build — Passport encounters are for builds you've seen in person.", 'info');
        setCode('');
        setOpen(false);
        return;
      }

      const subjectLabel = result.subject?.title || 'that build';
      if (result.dedupe_outcome === 'already_recorded') {
        void apiService.trackEvent('passport_encounter_duplicate', {
          metadata: { subject_type: result.subject?.subject_type },
        });
        showToast("You've already logged this one.", 'info');
      } else if (result.status === 'pending') {
        void apiService.trackEvent('passport_encounter_pending');
        showToast('Logged — pending validation.', 'info');
      } else {
        void apiService.trackEvent('passport_scan_completed', {
          metadata: { verification_level: result.verification_level },
        });
        showToast(`You saw ${subjectLabel} in person. ${verificationLevelLabel(result.verification_level)}.`, 'success');
      }
      setCode('');
      setOpen(false);
      onLogged?.();
      if (result.public_id) {
        navigate(`/passport/encounters/${encodeURIComponent(result.public_id)}`);
      }
    } catch (err: unknown) {
      const code = (err as { code?: string } | null)?.code;
      if (code === 'dedupe_conflict') {
        void apiService.trackEvent('passport_encounter_duplicate');
        showToast("You've already logged this one.", 'info');
        setCode('');
        setOpen(false);
        onLogged?.();
      } else {
        void apiService.trackEvent('passport_scan_failed');
        showToast(err instanceof Error ? err.message : 'Could not log that encounter', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)} className={className}>
        Log a build manually
      </Button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`form-section ${className}`}>
      <h2 className="form-section-title">Log a build manually</h2>
      <p className="text-xs text-gray-500">
        Paste a build's QR link or code. On mobile, use the camera scanner for the fastest way to log encounters.
      </p>
      <label htmlFor="passport-manual-code" className="input-label">
        Build URL or code
      </label>
      <input
        id="passport-manual-code"
        type="text"
        className="input"
        placeholder="https://biltspec.com/username/build or a printed code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        autoComplete="off"
      />
      <div className="flex gap-2">
        <Button type="submit" variant="primary" size="sm" loading={submitting} disabled={!code.trim()}>
          Log encounter
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default PassportScanPanel;
